import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { UploadCloud, CheckCircle2 } from "lucide-react";
import { API, api } from "@/lib/api";
import { trackEvent } from "@/lib/tracker";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";
import { FadeUp, Overline } from "@/components/Reveal";

const CATEGORIES = [
  "Enterprise GPU", "Data Centre GPU", "GPU Server", "AI Workstation", "Servers", "Workstations",
  "Processors", "Memory", "Storage", "Networking", "Computer Components", "Consumer Technology",
  "General Enquiry", "Other",
];
const INCOTERMS = ["", "EXW", "FCA", "FOB", "CIF", "DAP", "DDP"];
const CURRENCIES = ["", "USD", "EUR", "AED", "GBP"];
const ENHANCED = ["Enterprise GPU", "Data Centre GPU", "GPU Server", "AI Workstation", "Servers"];
const STANDARD = ["Processors", "Memory", "Storage", "Networking", "Computer Components", "Workstations", "Systems & Infrastructure"];
const END_USER_OPTIONS = [
  ["yes", "Yes — we are the final end user"],
  ["no_end_user", "No — we are purchasing for another end user"],
  ["no_channel", "No — we are a reseller / distributor / system integrator"],
  ["undetermined", "Not yet determined"],
];
const INTENDED_USES = [
  "AI training", "AI inference", "Data-centre deployment", "Cloud infrastructure", "Enterprise compute",
  "HPC / simulation", "Research", "University / education", "Healthcare", "Financial services",
  "Industrial engineering", "System integration", "Resale", "Internal IT", "Other",
];
const YES_NO = [["", "—"], ["no", "No"], ["yes", "Yes"], ["possibly", "Possibly"], ["unknown", "Unknown"]];
const CONTROL_QUESTIONS = [
  ["flag_government", "The end user is government-owned"],
  ["flag_military", "The end user is military or defence-related"],
  ["flag_nuclear", "The end user is involved in nuclear activities"],
  ["flag_semiconductor", "The end user is involved in advanced semiconductor development"],
  ["flag_surveillance", "The equipment is intended for military, intelligence or surveillance applications"],
  ["flag_third_party", "A third party is financing or directing the purchase"],
  ["flag_other_country", "The shipment is ultimately intended for another country"],
];

const inputCls =
  "w-full bg-white border border-midnight/15 px-4 py-3 text-sm text-midnight placeholder:text-midnight/35 outline-none focus:border-gold transition-colors";
const labelCls = "block text-xs font-bold uppercase tracking-[0.12em] text-midnight/50 mb-2";

const Field = ({ label, name, type = "text", required, placeholder, value, onChange, testId }) => (
  <div>
    <label className={labelCls} htmlFor={name}>{label}{required ? " *" : ""}</label>
    <input id={name} name={name} type={type} required={required} placeholder={placeholder} value={value} onChange={onChange} data-testid={testId || `rfq-${name}`} className={inputCls} />
  </div>
);

const Select = ({ label, name, options, value, onChange, required, testId }) => (
  <div>
    <label className={labelCls} htmlFor={name}>{label}{required ? " *" : ""}</label>
    <select id={name} required={required} value={value} onChange={onChange} data-testid={testId || `rfq-${name}`} className={inputCls}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </div>
);

export default function RequestPage() {
  const [params] = useSearchParams();
  const source = ["ai", "builder"].includes(params.get("source")) ? params.get("source") : "website";
  const [form, setForm] = useState({
    company_name: params.get("company") || "", contact_name: "", email: "", phone: "", website: "",
    country: "", category: params.get("category") || "", part_number: params.get("part_number") || "",
    description: params.get("product") || "", quantity: params.get("quantity") || "",
    target_price: "", currency: "", delivery_date: "", destination: params.get("destination") || "",
    incoterm: "", notes: params.get("notes") || "", confidentiality: false,
    end_user_status: "", eu_company: "", eu_country: "", eu_website: "", eu_activity: "", eu_address: "", eu_relationship: "",
    intended_use: "", intended_use_detail: "", install_country: "", install_city: "", facility: "",
    remain_at_location: "", reexport: "", reexport_details: "",
    flag_government: false, flag_military: false, flag_nuclear: false, flag_semiconductor: false,
    flag_surveillance: false, flag_third_party: false, flag_other_country: false,
    declaration: false,
  });
  const [file, setFile] = useState(null);
  const [complianceFiles, setComplianceFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [reference, setReference] = useState(null);
  const [settings, setSettings] = useState(null);
  const startedRef = useRef(false);

  const tier = ENHANCED.includes(form.category) ? "enhanced" : STANDARD.includes(form.category) ? "standard" : "light";

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const markStarted = () => {
    if (!startedRef.current) {
      startedRef.current = true;
      trackEvent("rfq_started", { source });
    }
  };

  const set = (k) => (e) => {
    markStarted();
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (tier !== "light") {
      if (!form.end_user_status) { toast.error("Please answer the end-user question in the qualification section."); return; }
      if (!form.declaration) { toast.error("Please confirm the end-use declaration to submit."); return; }
    }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, String(v)));
      fd.append("source", source);
      if (file) fd.append("file", file);
      complianceFiles.forEach((cf) => fd.append("compliance_files", cf));
      const res = await fetch(`${API}/rfq`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Submission failed");
      trackEvent("rfq_submitted", { source, category: form.category });
      if (file || complianceFiles.length) trackEvent("document_uploaded", { reference: data.reference });
      setReference(data.reference);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err.message || "Submission failed. Please try again.");
    }
    setBusy(false);
  };

  return (
    <div className="bg-white min-h-screen" data-testid="request-page">
      <Seo
        title="Request Hardware — Royal Deals FZ-LLC"
        description="Submit a commercial hardware requirement to Royal Deals FZ-LLC. Enterprise GPUs, AI infrastructure, processors, memory, storage and compute systems."
      />
      <Nav />
      <main className="pt-44 pb-24 bg-[#FAFAFA] min-h-screen">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10">
          {reference ? (
            <FadeUp>
              <div className="bg-white border border-gold/40 shadow-medium p-10 lg:p-14 text-center" data-testid="rfq-confirmation">
                <CheckCircle2 size={36} className="text-gold mx-auto mb-6" strokeWidth={1.5} />
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-4">Requirement Received</p>
                <h1 className="font-heading text-3xl md:text-4xl font-medium text-midnight">
                  Reference <span className="font-mono text-gold">{reference}</span>
                </h1>
                <p className="text-midnight/60 mt-6 max-w-xl mx-auto leading-relaxed">
                  Our commercial team will review your request and respond subject to product availability, compliance and commercial qualification.
                  {settings?.contact_email && <> You will hear from us at <span className="font-medium text-midnight">{settings.contact_email}</span>.</>}
                </p>
                <p className="text-sm text-midnight/55 mt-6">
                  Registered buyer?{" "}
                  <Link to="/register" data-testid="rfq-register-link" className="font-medium text-gold hover:text-midnight transition-colors">
                    Register your company →
                  </Link>
                </p>
                <p className="text-xs text-midnight/40 mt-8 leading-relaxed max-w-xl mx-auto">
                  Royal Deals FZ-LLC — Registered in Ras Al Khaimah, United Arab Emirates.
                  {settings?.address && <> Dubai Office: {settings.address.replace(/\n/g, ", ")}</>}
                </p>
              </div>
            </FadeUp>
          ) : (
            <FadeUp>
              <Overline>Request Hardware</Overline>
              <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-midnight">
                Submit a commercial requirement.
              </h1>
              <p className="text-midnight/55 mt-6 max-w-xl leading-relaxed">
                Provide as much detail as available. Manufacturer part numbers, target pricing and delivery terms accelerate qualification.
              </p>

              <form onSubmit={submit} className="mt-12 space-y-10 bg-white hairline shadow-soft p-8 lg:p-12" data-testid="rfq-form">
                <fieldset>
                  <legend className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-6">01 — Requirement</legend>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls} htmlFor="category">Product / Category</label>
                      <select id="category" value={form.category} onChange={set("category")} data-testid="rfq-category" className={inputCls}>
                        <option value="">Select category…</option>
                        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <Field label="Manufacturer Part Number (if known)" name="part_number" value={form.part_number} onChange={set("part_number")} placeholder="e.g. exact SKU / PN" />
                    <div className="sm:col-span-2">
                      <label className={labelCls} htmlFor="description">Description</label>
                      <textarea id="description" rows={3} value={form.description} onChange={set("description")} data-testid="rfq-description" placeholder="Product class, memory, configuration…" className={inputCls} />
                    </div>
                    <Field label="Quantity" name="quantity" value={form.quantity} onChange={set("quantity")} placeholder="e.g. 200" />
                    <div className="grid grid-cols-2 gap-5">
                      <Field label="Target Price" name="target_price" value={form.target_price} onChange={set("target_price")} placeholder="Optional" />
                      <div>
                        <label className={labelCls} htmlFor="currency">Currency</label>
                        <select id="currency" value={form.currency} onChange={set("currency")} data-testid="rfq-currency" className={inputCls}>
                          {CURRENCIES.map((c) => <option key={c} value={c}>{c || "—"}</option>)}
                        </select>
                      </div>
                    </div>
                    <Field label="Required Delivery Date" name="delivery_date" type="date" value={form.delivery_date} onChange={set("delivery_date")} />
                    <div>
                      <label className={labelCls} htmlFor="incoterm">Preferred Incoterm</label>
                      <select id="incoterm" value={form.incoterm} onChange={set("incoterm")} data-testid="rfq-incoterm" className={inputCls}>
                        {INCOTERMS.map((c) => <option key={c} value={c}>{c || "—"}</option>)}
                      </select>
                    </div>
                    <Field label="Destination Country" name="destination" value={form.destination} onChange={set("destination")} placeholder="e.g. United Arab Emirates" />
                  </div>
                </fieldset>

                <fieldset>
                  <legend className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-6">02 — Company</legend>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Company Name" name="company_name" required value={form.company_name} onChange={set("company_name")} />
                    <Field label="Contact Name" name="contact_name" required value={form.contact_name} onChange={set("contact_name")} />
                    <Field label="Business Email" name="email" type="email" required value={form.email} onChange={set("email")} />
                    <Field label="Telephone / WhatsApp" name="phone" value={form.phone} onChange={set("phone")} />
                    <Field label="Company Website" name="website" value={form.website} onChange={set("website")} placeholder="https://" />
                    <Field label="Country of Registration" name="country" value={form.country} onChange={set("country")} />
                  </div>
                </fieldset>

                {tier !== "light" && (
                  <fieldset data-testid="qualification-section">
                    <legend className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-2">03 — End User &amp; Deployment Qualification</legend>
                    <p className="text-xs text-midnight/45 mb-6 leading-relaxed">
                      {tier === "enhanced"
                        ? "Advanced compute hardware requires enhanced end-user qualification. Answers trigger compliance review — they do not automatically decline your enquiry."
                        : "Standard qualification for enterprise hardware. Answers help our compliance review — they do not automatically decline your enquiry."}
                    </p>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div className="sm:col-span-2">
                        <Select label="Is your company the final end user of the equipment?" name="end_user_status" required options={[["", "Select…"], ...END_USER_OPTIONS]} value={form.end_user_status} onChange={set("end_user_status")} />
                      </div>
                      {form.end_user_status.startsWith("no") && (
                        <>
                          <Field label="End-User Company Name" name="eu_company" value={form.eu_company} onChange={set("eu_company")} />
                          <Field label="End-User Country" name="eu_country" value={form.eu_country} onChange={set("eu_country")} />
                          <Field label="End-User Website" name="eu_website" value={form.eu_website} onChange={set("eu_website")} />
                          <Field label="End-User Business Activity" name="eu_activity" value={form.eu_activity} onChange={set("eu_activity")} />
                          <Field label="End-User Address" name="eu_address" value={form.eu_address} onChange={set("eu_address")} />
                          <Field label="Relationship to Purchaser" name="eu_relationship" value={form.eu_relationship} onChange={set("eu_relationship")} placeholder="e.g. customer, project partner" />
                        </>
                      )}
                      <Select label="Intended application" name="intended_use" required options={[["", "Select…"], ...INTENDED_USES.map((u) => [u, u])]} value={form.intended_use} onChange={set("intended_use")} />
                      <Field label="Final installation country" name="install_country" value={form.install_country} onChange={set("install_country")} />
                      <div className="sm:col-span-2">
                        <label className={labelCls} htmlFor="intended_use_detail">Describe the intended use in more detail</label>
                        <textarea id="intended_use_detail" rows={2} value={form.intended_use_detail} onChange={set("intended_use_detail")} data-testid="rfq-intended-use-detail" className={inputCls} />
                      </div>
                      <Field label="Final installation city" name="install_city" value={form.install_city} onChange={set("install_city")} />
                      <Field label="Deployment facility / data centre (if known)" name="facility" value={form.facility} onChange={set("facility")} />
                      <Select label="Will the equipment remain at that location?" name="remain_at_location" options={YES_NO.filter(([v]) => v !== "possibly" && v !== "unknown")} value={form.remain_at_location} onChange={set("remain_at_location")} />
                      <Select label="Will the products be re-exported, transferred or resold after delivery?" name="reexport" options={YES_NO} value={form.reexport} onChange={set("reexport")} testId="rfq-reexport" />
                      {(form.reexport === "yes" || form.reexport === "possibly") && (
                        <div className="sm:col-span-2">
                          <label className={labelCls} htmlFor="reexport_details">Destination / recipient details</label>
                          <textarea id="reexport_details" rows={2} value={form.reexport_details} onChange={set("reexport_details")} data-testid="rfq-reexport-details" className={inputCls} />
                        </div>
                      )}
                    </div>

                    {tier === "enhanced" && (
                      <div className="mt-6 border border-midnight/10 p-5 space-y-3" data-testid="control-questions">
                        <p className="text-xs font-bold uppercase tracking-[0.15em] text-midnight/50">Ownership &amp; control</p>
                        {CONTROL_QUESTIONS.map(([k, q]) => (
                          <label key={k} className="flex items-start gap-3 cursor-pointer">
                            <input type="checkbox" checked={form[k]} onChange={set(k)} data-testid={`rfq-${k}`} className="w-4 h-4 mt-0.5 accent-[#C5A059]" />
                            <span className="text-sm text-midnight/65">{q}</span>
                          </label>
                        ))}
                      </div>
                    )}

                    <div className="mt-6">
                      <label className={labelCls}>Compliance documents (EUC / EUS / PO / company registration / project documentation — optional, max 15MB each)</label>
                      <label htmlFor="rfq-compliance-files" className="flex items-center gap-4 border border-dashed border-midnight/25 px-5 py-5 cursor-pointer hover:border-gold transition-colors bg-white" data-testid="rfq-compliance-upload">
                        <UploadCloud size={18} className="text-midnight/40" />
                        <span className="text-sm text-midnight/50">
                          {complianceFiles.length ? complianceFiles.map((f) => f.name).join(", ") : "Attach end-user or compliance documents"}
                        </span>
                        <input id="rfq-compliance-files" type="file" multiple className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.csv,.doc,.docx" onChange={(e) => setComplianceFiles(Array.from(e.target.files || []))} data-testid="rfq-compliance-files-input" />
                      </label>
                    </div>

                    <label className="flex items-start gap-3 cursor-pointer mt-6" data-testid="rfq-declaration">
                      <input type="checkbox" checked={form.declaration} onChange={set("declaration")} className="w-4 h-4 mt-0.5 accent-[#C5A059]" />
                      <span className="text-sm text-midnight/65 leading-relaxed">
                        I confirm that the information provided regarding the purchaser, end user, destination and intended use is accurate to the best of my knowledge. I understand that certain products may be subject to export controls, destination restrictions, end-use restrictions or additional compliance review. *
                      </span>
                    </label>
                  </fieldset>
                )}

                <fieldset>
                  <legend className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-6">{tier !== "light" ? "04" : "03"} — Supporting Detail</legend>
                  <div className="space-y-5">
                    <div>
                      <label className={labelCls} htmlFor="notes">Additional Notes</label>
                      <textarea id="notes" rows={3} value={form.notes} onChange={set("notes")} data-testid="rfq-notes" className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Upload PO / Requirement Document (optional, max 15MB)</label>
                      <label htmlFor="rfq-file" className="flex items-center gap-4 border border-dashed border-midnight/25 px-5 py-6 cursor-pointer hover:border-gold transition-colors bg-white" data-testid="rfq-upload-zone">
                        <UploadCloud size={20} className="text-midnight/40" />
                        <span className="text-sm text-midnight/50">{file ? file.name : "PDF, image or document"}</span>
                        <input id="rfq-file" type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.csv,.doc,.docx" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="rfq-file-input" />
                      </label>
                    </div>
                    <label className="flex items-center gap-3 cursor-pointer" data-testid="rfq-confidentiality">
                      <input type="checkbox" checked={form.confidentiality} onChange={set("confidentiality")} className="w-4 h-4 accent-[#C5A059]" />
                      <span className="text-sm text-midnight/65">I require a confidentiality agreement</span>
                    </label>
                  </div>
                </fieldset>

                <button type="submit" disabled={busy} data-testid="rfq-submit" className="w-full bg-midnight text-white text-sm font-semibold tracking-wide px-8 py-4 hover:bg-midnight-light transition-colors disabled:opacity-50">
                  {busy ? "Submitting…" : "Submit Commercial Requirement"}
                </button>
                <p className="text-xs text-midnight/40 text-center">
                  Submitting a requirement does not subscribe you to marketing communications.
                </p>
              </form>
            </FadeUp>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
